<script setup>
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { templates } from '@/stores/websiteBuilderStore'
import { API_BASE_URL } from '@/stores/appConfig'
import { injectRawSiteShims } from '@/utils/rawSiteShims'
import SiteLoadingSpinner from '@/components/SiteLoadingSpinner.vue'



const route = useRoute()

const loading = ref(true)
const loadError = ref('')
const hasLoaded = ref(false)
const website = reactive(createEmptyWebsite())

// Increments for each load attempt so stale async responses cannot overwrite newer route data.
let loadRun = 0

const routeIdentifier = computed(() =>
  String(route.params.id || '').trim()
)

const selectedTemplate = computed(() => {
  return templates.find(t => t.id === website.template_id) || null
})

// Sites built from raw HTML (Builder3's AI generation or a pasted-in page)
// skip the JSON-block/Vue-template pipeline entirely — the whole document is
// stored as-is in website.content.html and rendered in a sandboxed iframe.
//
// Sandbox note: this iframe deliberately omits allow-same-origin (unlike
// Builder3's editor preview, which needs it for click-to-edit DOM access).
// allow-scripts + NOT allow-same-origin gives the document an opaque/null
// origin — real JS runs (routers, menus, form handlers, whatever the page
// needs), but it cannot read quotastic.ca's cookies/localStorage or reach
// window.parent, since it isn't considered the same origin at all. That's
// what lets us safely run someone else's/an AI's script here.
const RAW_HTML_TEMPLATE_ID = 'raw-html'
const isRawHtmlSite = computed(() => website.template_id === RAW_HTML_TEMPLATE_ID)

const rawSiteHtml = computed(() => injectRawSiteShims(website.content?.html))

const renderedContent = computed(() => {
  const templateContent = cloneContent(selectedTemplate.value?.content || {})
  const savedContent = parseContent(website.content)

  return mergeContent(templateContent, savedContent)
})

const pageTitle = computed(() =>
  text(
    website.site_title ||
      renderedContent.value?.seo?.title ||
      renderedContent.value?.hero?.hero_headline ||
      (isRawHtmlSite.value ? website.content?.title : '') ||
      website.domain ||
      website.quotastic_domain ||
      selectedTemplate.value?.name ||
      'Published Site'
  )
)

watch(
  pageTitle,
  (title) => {
    if (title) {
      document.title = title
    }
  },
  { immediate: true }
)

const pageDescription = computed(() =>
  text(
    website.meta_description ||
      renderedContent.value?.seo?.description ||
      renderedContent.value?.hero?.hero_sub ||
      (isRawHtmlSite.value ? website.content?.description : '') ||
      ''
  )
)

const pageUrl = computed(() => window.location.href)

function setMetaTag(attr, key, content) {
  if (!content) return

  let tag = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

watch(
  [pageTitle, pageDescription, pageUrl],
  ([title, description, url]) => {
    setMetaTag('name', 'description', description)
    setMetaTag('property', 'og:type', 'website')
    setMetaTag('property', 'og:title', title)
    setMetaTag('property', 'og:description', description)
    setMetaTag('property', 'og:url', url)
    setMetaTag('name', 'twitter:card', 'summary')
    setMetaTag('name', 'twitter:title', title)
    setMetaTag('name', 'twitter:description', description)
  },
  { immediate: true }
)

const componentKey = computed(() =>
  [
    website.id || routeIdentifier.value || 'site',
    website.template_id || selectedTemplate.value?.id || 'template'
  ].join(':')
)

function createEmptyWebsite() {
  return {
    id: null,
    company_id: null,
    domain: '',
    quotastic_domain: '',
    site_title: '',
    meta_description: '',
    template_id: '',
    content: null,
  }
}

function resetWebsite() {
  Object.assign(website, createEmptyWebsite())
}

function hydrateWebsite(record) {
  resetWebsite()
  Object.assign(website, {
    ...record,
    content: parseContent(record.content),
  })
}

function parseContent(rawContent) {
  if (!rawContent) {
    return {}
  }

  if (typeof rawContent === 'string') {
    try {
      return JSON.parse(rawContent)
    }
    catch (error) {
      console.error('Unable to parse website content:', error)
      return {}
    }
  }

  if (typeof rawContent === 'object') {
    return rawContent
  }

  return {}
}

function cloneContent(value) {
  if (!value) {
    return {}
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value)
  }

  return JSON.parse(JSON.stringify(value))
}

function isPlainObject(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  )
}

function isFieldDescriptor(value) {
  return isPlainObject(value) && 'value' in value
}

function mergeContent(baseContent, savedContent) {
  if (isFieldDescriptor(baseContent)) {
    if (isFieldDescriptor(savedContent)) {
      return {
        ...baseContent,
        ...savedContent,
        value: mergeContent(baseContent.value, savedContent.value)
      }
    }

    return savedContent == null
      ? baseContent
      : {
          ...baseContent,
          value: savedContent
        }
  }

  if (Array.isArray(baseContent) || Array.isArray(savedContent)) {
    return Array.isArray(savedContent) ? savedContent : baseContent
  }

  if (!isPlainObject(baseContent) || !isPlainObject(savedContent)) {
    return savedContent ?? baseContent
  }

  const merged = { ...baseContent }

  for (const key of Object.keys(savedContent)) {
    merged[key] = key in baseContent
      ? mergeContent(baseContent[key], savedContent[key])
      : savedContent[key]
  }

  return merged
}

function uniqueQueries(identifier) {
  const encoded = encodeURIComponent(identifier)
  const queries = [
    `quotastic_domain=${encoded}`,
    `domain=${encoded}`,
    `company_id=${encoded}`,
  ]

  if (/^\d+$/.test(identifier)) {
    queries.splice(2, 0, `id=${encoded}`)
  }

  return [...new Set(queries)]
}

async function fetchWebsite(query) {
  const response = await fetch(`${API_BASE_URL}/websites?${query}`)
  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data.error || 'Failed to load website')
  }

  return data.websites?.[0] || null
}

async function retrieveWebsite(identifier = routeIdentifier.value) {
  const currentRun = ++loadRun
  const siteIdentifier = String(identifier || '').trim()

  resetWebsite()
  loading.value = true
  loadError.value = ''
  hasLoaded.value = false

  if (!siteIdentifier) {
    loading.value = false
    hasLoaded.value = true
    loadError.value = 'No site was specified.'
    return null
  }

  try {
    for (const query of uniqueQueries(siteIdentifier)) {
      const record = await fetchWebsite(query)

      if (currentRun !== loadRun) {
        return null
      }

      if (record) {
        if (record.version_id == null) {
          loadError.value = "This site hasn't been published yet."
          return null
        }

        hydrateWebsite(record)
        return record
      }
    }

    loadError.value = 'Site not found.'
    return null
  }
  catch (error) {
    if (currentRun !== loadRun) {
      return null
    }

    console.error('retrieveWebsite error:', error)
    loadError.value = error.message || 'Failed to load website.'
    return null
  }
  finally {
    if (currentRun === loadRun) {
      loading.value = false
      hasLoaded.value = true
    }
  }
}

function baseData(siteContent) {
  return {
    content: cloneContent(siteContent || {}),
    scrolled: false,
    menuOpen: false,
    formSent: false,
    formSubmitting: false,
    formError: '',
    formConfirmation: null,
    form: {
      name: '',
      email: '',
      service: '',
      message: ''
    }
  }
}

function contactEmailFromContent(content) {
  const email = content?.contact?.email || content?.contact_email || ''

  if (email && typeof email === 'object') {
    return email.value || ''
  }

  return email || ''
}

function text(value) {
  if (value && typeof value === 'object' && 'value' in value) {
    return text(value.value)
  }

  return String(value || '').trim()
}

const compiledComponent = computed(() => {
  const site = selectedTemplate.value

  if (!hasLoaded.value || loading.value || loadError.value || !site) {
    return null
  }

  return defineComponent({
    template: site.template,

    data() {
      return baseData(renderedContent.value)
    },

    methods: {
      async submitForm(e) {
        e?.preventDefault?.()

        if (this.formSubmitting) {
          return null
        }

        const routeId = routeIdentifier.value
        const payload = {
          site_id: website.id || routeId || null,
          ...(website.company_id ? { company_id: website.company_id } : {}),
          name: this.form.name,
          email: this.form.email,
          service: this.form.service,
          message: this.form.message,
          contractor_email: contactEmailFromContent(this.content),
          page_url: window.location.href
        }

        this.formSubmitting = true
        this.formSent = false
        this.formError = ''
        this.formConfirmation = null

        try {
          const response = await fetch(`${API_BASE_URL}/form-submission`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          })
          const confirmation = await response.json().catch(() => null)

          if (!response.ok) {
            throw new Error(
              confirmation?.message ||
              confirmation?.error ||
              'Unable to submit form.'
            )
          }

          this.formSent = true
          this.formConfirmation = confirmation
          this.form = {
            name: '',
            email: '',
            service: '',
            message: ''
          }

          setTimeout(() => {
            this.formSent = false
          }, 3000)

          return confirmation
        }
        catch (error) {
          this.formError = error.message || 'Unable to submit form.'
          console.error('Form submission failed:', error)

          return null
        }
        finally {
          this.formSubmitting = false
        }
      }
    },

    mounted() {
      this._scrollTarget =
        document.querySelector('.content') || window

      this._onScroll = () => {
        const scrollTop =
          this._scrollTarget === window
            ? window.scrollY
            : this._scrollTarget.scrollTop

        this.scrolled = scrollTop > 0
      }

      this._onScroll()
      this._scrollTarget.addEventListener('scroll', this._onScroll)
    },

    unmounted() {
      this._scrollTarget?.removeEventListener(
        'scroll',
        this._onScroll
      )
    }
  })
})

watch(
  routeIdentifier,
  (identifier) => {
    retrieveWebsite(identifier)
  },
  { immediate: true }
)

const resolvedCss = computed(() => {
  const css = selectedTemplate.value?.css
  if (!css) return ''

  return css.replace(/\{\{content\.([^}]+)\}\}/g, (_match, path) => {
    const parts = path.trim().split('.')
    let val = renderedContent.value
    for (const part of parts) {
      val = val?.[part]
      if (val === undefined || val === null) return ''
    }
    return typeof val === 'object' ? (val.value ?? '') : String(val)
  })
})

const cssKey = ref(0)
watch(resolvedCss, () => { cssKey.value++ })
</script>

<template>
  <div class="site-view">
    <SiteLoadingSpinner v-if="loading" />

    <div
      v-else-if="loadError"
      class="site-status site-status-error"
    >
      {{ loadError }}
    </div>

    <iframe
      v-else-if="isRawHtmlSite && rawSiteHtml"
      class="raw-site-frame"
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      :srcdoc="rawSiteHtml"
    />

    <template v-else-if="selectedTemplate && compiledComponent">
      <component :is="'style'" :key="cssKey">
        {{ resolvedCss }}
      </component>

      <component
        :is="compiledComponent"
        :key="componentKey"
      />
    </template>

    <div
      v-else
      class="site-status site-status-error"
    >
      Site content not found.
    </div>
  </div>
</template>

<style scoped>
.site-view {
  min-height: 100vh;
}

.site-status {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 32px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: 600 1rem/1.4 system-ui, sans-serif;
}

.site-status-error {
  color: #9f2a1d;
}

.raw-site-frame {
  display: block;
  width: 100%;
  height: 100vh;
  border: none;
  background: #fff;
}
</style>
